/**
 * YearEndLoginPrompt
 *
 * 비로그인 사용자에게 로그인 유도
 * - 로그인 후 /year-end로 복귀
 */

import React from 'react';
import { Stack, Typography, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useThreadsAuth } from '../../hooks/useThreadsAuth';
import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';

export const YearEndLoginPrompt = ({ year }) => {
  const { t } = useTranslation();
  const { login } = useThreadsAuth();

  const handleLogin = () => {
    // 로그인 후 돌아올 경로 저장
    sessionStorage.setItem('login_redirect_to', '/year-end');
    login();
  };

  return (
    <Stack
      spacing={4}
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: '70vh',
        textAlign: 'center',
        px: 3,
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          width: 100,
          height: 100,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AutoGraphOutlinedIcon
          sx={{ fontSize: 50, color: 'white' }}
        />
      </Box>

      {/* Title */}
      <Stack spacing={1}>
        <Typography variant="h4" fontWeight={700}>
          {t('yearEnd.loginPrompt.title', { year })}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 400 }}
        >
          {t('yearEnd.loginPrompt.description')}
          <br />
          {t('yearEnd.loginPrompt.features')}
        </Typography>
      </Stack>

      {/* Features */}
      <Stack spacing={1} sx={{ maxWidth: 300 }}>
        <FeatureItem emoji="🏆" text={t('yearEnd.loginPrompt.feature1')} />
        <FeatureItem emoji="👥" text={t('yearEnd.loginPrompt.feature2')} />
        <FeatureItem emoji="📊" text={t('yearEnd.loginPrompt.feature3')} />
        <FeatureItem emoji="🎯" text={t('yearEnd.loginPrompt.feature4')} />
      </Stack>

      {/* Login Button */}
      <Button
        variant="contained"
        size="large"
        onClick={handleLogin}
        sx={{
          px: 4,
          py: 1.5,
          borderRadius: 3,
          fontSize: '1rem',
          fontWeight: 600,
        }}
      >
        {t('yearEnd.loginPrompt.loginButton')}
      </Button>

      {/* Note */}
      <Typography variant="caption" color="text.disabled">
        {t('yearEnd.loginPrompt.note')}
      </Typography>
    </Stack>
  );
};

// Feature 아이템 컴포넌트
const FeatureItem = ({ emoji, text }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      py: 0.5,
    }}
  >
    <Typography variant="body1">{emoji}</Typography>
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Box>
);

export default YearEndLoginPrompt;
